import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';
import cn from 'classnames';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { getPricing, getLockInText } from '~/Config/Stripe';
import config from '~/Config';
import Card from '~/Components/Layout/Card/Card';
import StripePermittedView from '~/Components/Global/StripePermittedView';
import { guid, useMountEffect } from '~/util';
import Storage from '~/Providers/Storage';
import Spinner from '~/Components/Global/Loading/Spinner';
import { subscribe, updateField } from '~/Redux/Modules/Subscription';
import useSetupIntent from '~/Components/Global/Stripe/useSetupIntent';
import mParticle, { ScreenViewEvent } from '~/Providers/mParticle';
import SetupIntentForm from './SetupIntentForm';

import './Subscription.scss';

const stripePromise = loadStripe(config.stripeKey, config.stripeOptions);

const SubscriptionWithPaymentElement = () => {
  const [pricing, setPricing] = useState(getPricing());
  const [lockInText] = useState(getLockInText());
  const [eventId, setEventId] = useState(Storage.get('subscriptionEventId'));
  const { setupIntent, error: setupIntentError, setSetupIntentId } = useSetupIntent(true);
  const [postalCode, setPostalCode] = useState('');
  const dispatch = useDispatch();
  const {
    isFetching,
    data: { coupon, plan },
  } = useSelector(({ Subscription }) => Subscription);
  const user = useSelector(({ User }) => User.data);

  useMountEffect(() => {
    if (!eventId) {
      const newId = guid();
      Storage.set('subscriptionEventId', newId);
      setEventId(newId);
    }
    mParticle.logEvent(new ScreenViewEvent('Manage Subscription'));
  });

  const handleFieldChanged = useCallback(
    change => {
      if (change.postal_code) {
        setPostalCode(change.postal_code);
      }
      dispatch(updateField(change));
    },
    [dispatch, setPostalCode, updateField]
  );

  useEffect(() => {
    handleFieldChanged({ plan: pricing.yearly });
  }, [pricing, handleFieldChanged]);

  const handleSubmit = paymentMethod => {
    dispatch(
      subscribe({
        paymentMethod,
        coupon: coupon,
        plan: plan.plan,
        price: plan.price,
        userId: user.id,
        eventId,
        postalCode,
      })
    );
  };

  const renderTrialText = () => {
    if (!user.isEligibleForTrial) {
      return <h2 className="center">Thank you for trying Artful Agenda!</h2>;
    }
    if (user.trial_ends_at == null) {
      return (
        <>
          <h2 className="center">Try Artful Agenda FREE for two weeks!</h2>
          <p className="center">Your card will not be charged until the end of your free trial.</p>
        </>
      );
    }
    return (
      <>
        <h2 className="center">Try Artful Agenda again for two weeks!</h2>
        <p className="center">Your card will not be charged until the end of your free trial.</p>
      </>
    );
  };

  return (
    <StripePermittedView
      disabledView={
        <Card noLogo>
          <div className="subscriptions">
            {(user.trial_ends_at != null && (
              <>
                <h2 className="center">Thank you for trying Artful Agenda!</h2>
                <hr />
              </>
            )) || (
              <>
                <h2 className="center">Try Artful Agenda FREE for two weeks!</h2>
                <hr />
              </>
            )}
            <p className="center margin-bottom">
              Please use the mobile app to set up your subscription.
            </p>
          </div>
        </Card>
      }
    >
      <Card noLogo className="card-wide">
        <div className="subscription">
          {renderTrialText()}
          <hr />

          <h3 className="center slight-margin-bottom">PRICING:</h3>

          {lockInText && <p>{lockInText}</p>}
          <div className="flex-row margin-bottom">
            <div className="price-wrapper">
              <div
                className={cn('plan year no-scale active')}
                onClick={() => handleFieldChanged({ plan: pricing.yearly })}
              >
                <span className="price">${pricing.yearly.displayPrice}</span>
                <span>{pricing.yearly.displayUnits}</span>
              </div>

              <h5 className="ad">({pricing.yearly.subtext})</h5>
            </div>
          </div>
          <div className="banner-wrapper">
            <div className="banner">
              <h6>Happiness Guaranteed!</h6>
              <span>Cancel at any time!</span>
              <span className="banner-triangle-left-container">
                <span className="banner-triangle-left" />
              </span>
              <span className="banner-triangle-right-container">
                <span className="banner-triangle-right" />
              </span>
            </div>
          </div>
          <h5 className="center margin-bottom">PAYMENT INFORMATION</h5>
          {setupIntent && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: setupIntent.clientSecret,
                appearance: { variables: { fontSizeBase: '14px' } },
                loader: 'always',
              }}
              key={setupIntent.clientSecret}
            >
              <SetupIntentForm
                isFetching={isFetching}
                user={user}
                intent={setupIntent}
                setSetupIntentId={setSetupIntentId}
                amount={plan.price}
                plan={plan.plan}
                onSubmit={handleSubmit}
                showCoupon
                coupon={coupon}
                updateField={handleFieldChanged}
                postalCode={postalCode}
              />
            </Elements>
          )}
          {setupIntentError && (
            <div className="text-danger">
              An error occurred while setting up payments. Please try again later.
            </div>
          )}
          {!setupIntent && !setupIntentError && <Spinner />}
          <input id="artfulUserId" type="hidden" name="userId" value={user.id} />
          <input id="artfulEventId" type="hidden" name="eventId" value={eventId} />
          <div className="disclosure">
            {`By signing up, you agree to Artful Agenda's`}{' '}
            <a href="http://artfulagenda.com/terms" className="terms">
              Terms of Service
            </a>
          </div>
        </div>
      </Card>
    </StripePermittedView>
  );
};

SubscriptionWithPaymentElement.propTypes = {
  updateField: propTypes.func,
  isFetching: propTypes.bool,
  name: propTypes.string,
  coupon: propTypes.string,
  subscribe: propTypes.func,
  plan: propTypes.string,
  user: propTypes.object,
};

SubscriptionWithPaymentElement.defaultProps = {};

export default SubscriptionWithPaymentElement;
